// Team Chat als Deck-Seite der Pille im iMessage-Stil (Handoff Pille §5, Screen 15).
// Links die Gespräche, rechts der Verlauf — auf schmalen Schirmen nacheinander
// (Liste → Verlauf → „‹ Chats“), damit auf dem Handy nichts gequetscht wird.
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, CaretLeft, ChatCircle, FileText, ImageSquare, SpinnerGap, UsersThree, X } from '@phosphor-icons/react';
import { FARBE } from '../pille-stil';
import { figgoSignal } from '../figgoSignal';
import { useChat } from '../../../contexts/ChatContext';
import {
  AUFBEWAHRUNG_TAGE, gruppenName, kollegeName, verlaufInGruppe, verlaufMit, zielSchluessel,
  type ChatGruppe, type Kollege,
} from '../../../lib/chat/nachrichten';
import { bildUrl, ladeBildHoch } from '../../../lib/chat/api';
import { istBildDatei } from '../../../lib/chat/nachrichten';
import { ladeChatDatei, oeffneChatAnhang, oeffneChatDatei } from '../../../lib/chat/anhangOeffnen';
import { InAkteAblegenDialog } from '../../dokumente/InAkteAblegenDialog';
import { Avatar } from './chat/Avatar';
import { GespraechsListe } from './chat/GespraechsListe';
import { Verlauf } from './chat/Verlauf';
import { BildGross } from './chat/BildGross';
import { GruppenKopf } from './chat/GruppenKopf';

type Ziel = { art: 'person' | 'gruppe'; id: string };

export function ChatPage() {
  const {
    meineId, kollegen, nachrichten, gruppen, unterhaltungen, senden, gelesen, loeschen, gruppenNeuLaden,
    zuOeffnendesZiel, zielGeoeffnet,
  } = useChat();
  const [ziel, setZiel] = useState<Ziel | null>(null);
  const [entwurf, setEntwurf] = useState('');
  const [bild, setBild] = useState<{ path: string; name: string } | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [gross, setGross] = useState<{ url: string; path: string; name: string | null } | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // #549: im Chat geschickte Datei in die Akte legen
  const [ablegen, setAblegen] = useState<{ path: string; name: string } | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const dateiRef = useRef<HTMLInputElement>(null);
  const endeRef = useRef<HTMLDivElement>(null);

  const namen = useMemo(() => {
    const m = new Map<string, Kollege>();
    for (const k of kollegen) m.set(k.id, k);
    return m;
  }, [kollegen]);

  const verlauf = useMemo(() => {
    if (!ziel) return [];
    if (ziel.art === 'gruppe') return verlaufInGruppe(nachrichten, ziel.id);
    return meineId ? verlaufMit(nachrichten, meineId, ziel.id) : [];
  }, [nachrichten, meineId, ziel]);

  // Beim Öffnen einer Unterhaltung quittieren + ans Ende springen.
  useEffect(() => {
    if (!ziel) return;
    void gelesen(ziel);
    endeRef.current?.scrollIntoView({ block: 'end' });
  }, [ziel, gelesen]);

  useEffect(() => {
    if (ziel) endeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [verlauf.length, ziel]);

  // Wechsel der Unterhaltung: Entwurf, Anhang und Fehlermeldung leeren. Bei 1:1 war ein
  // stehen gebliebener Satz harmlos, mit Gruppen kann so ein halbfertiger Text versehentlich
  // an ein ganzes Team gehen.
  //
  // Der Schlüssel statt `ziel` selbst: jede Auswahl baut ein NEUES { art, id }, auch für das
  // bereits offene Gespräch. An `ziel` gehängt feuerte der Effekt deshalb auch dann und warf
  // den getippten Entwurf weg — bei einem Klick auf die offene Unterhaltung, beim Anlegen
  // einer Gruppe und beim Klick auf die Nachrichten-Vorschau in der Pille.
  const zielKey = zielSchluessel(ziel);
  useEffect(() => {
    setEntwurf('');
    setBild(null);
    setFehler(null);
  }, [zielKey]);

  // Von außen angefordertes Gespräch (Klick auf die Nachrichten-Vorschau in der Pille).
  // Nach dem Übernehmen zurückmelden, sonst überschriebe dieselbe Anforderung jede
  // spätere eigene Auswahl wieder.
  useEffect(() => {
    if (!zuOeffnendesZiel) return;
    setZiel(zuOeffnendesZiel);
    setNeuOffen(false);
    zielGeoeffnet();
  }, [zuOeffnendesZiel, zielGeoeffnet]);

  // Escape schließt das Vollbild (und nicht gleich die ganze Pille).
  useEffect(() => {
    if (!gross) return;
    const zu = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setGross(null); } };
    window.addEventListener('keydown', zu, true);
    return () => window.removeEventListener('keydown', zu, true);
  }, [gross]);

  async function handleSenden() {
    if (!ziel || laedt) return;
    if (!entwurf.trim() && !bild) return;
    setLaedt(true);
    setFehler(null);
    const ok = await senden(ziel, entwurf, bild);
    setLaedt(false);
    if (ok) { setEntwurf(''); setBild(null); figgoSignal('send', { force: true, p: 0.85 }); }
    else setFehler('Nachricht konnte nicht gesendet werden — bitte erneut versuchen.');
  }

  async function handleBild(datei: File | undefined) {
    if (!datei || !meineId) return;
    setLaedt(true);
    setFehler(null);
    const hoch = await ladeBildHoch(meineId, datei);
    setLaedt(false);
    if (hoch.ok) setBild({ path: hoch.path, name: hoch.name });
    else setFehler(hoch.fehler);
  }

  // Speichern: eigene signierte URL mit Download-Kopfzeile, damit der Browser die Datei
  // ablegt statt sie anzuzeigen (der Bucket ist privat, ein <a download> allein reicht nicht).
  async function herunterladen(b: { path: string; name: string | null }) {
    const url = await bildUrl(b.path, b.name || 'bild.jpg');
    if (!url) { setFehler('Bild konnte nicht geladen werden.'); return; }
    const a = document.createElement('a');
    a.href = url;
    a.download = b.name || 'bild.jpg';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  // Neue Gruppe: sofort auswählen, „Neu" schließen, Gruppenliste nachziehen.
  function gruppeGegruendet(g: ChatGruppe) {
    setNeuOffen(false);
    setZiel({ art: 'gruppe', id: g.id });
    void gruppenNeuLaden();
  }

  // Verlassen: Auswahl leeren, sonst zeigte die Ansicht einen Verlauf, den RLS bereits
  // gesperrt hat (die Gruppe ist danach nicht mehr sichtbar).
  function gruppeVerlassen() {
    setZiel(null);
    void gruppenNeuLaden();
  }

  const partner = ziel?.art === 'person' ? namen.get(ziel.id) : null;
  const gruppe = ziel?.art === 'gruppe' ? gruppen.find((g) => g.id === ziel.id) : null;
  const kopfName = ziel?.art === 'gruppe' ? (gruppe ? gruppenName(gruppe) : 'Gruppe') : kollegeName(partner);
  // Personen, die nicht mehr in der Kollegenliste stehen, und Gruppen, die verlassen/gelöscht
  // wurden, verschwinden aus der Liste — die Nachrichten selbst bleiben im Bestand.
  const offeneListe = unterhaltungen.filter((u) =>
    u.art === 'gruppe' ? gruppen.some((g) => g.id === u.id) : namen.has(u.id));

  const zurueckKnopf = (
    <button
      type="button"
      onClick={() => setZiel(null)}
      className="flex shrink-0 cursor-pointer items-center sm:hidden"
      style={{ height: 36, gap: 2, border: 'none', background: 'transparent', color: FARBE.gold, fontFamily: 'inherit', fontSize: 15 }}
      aria-label="Zurück zu den Chats"
    >
      <CaretLeft size={18} weight="bold" /> Chats
    </button>
  );
  const kannSenden = !laedt && (!!entwurf.trim() || !!bild);

  return (
    <div className="flex h-full text-white" style={{ gap: 12, padding: '0 14px 14px' }}>
      {/* ── Gespräche ── */}
      <GespraechsListe
        unterhaltungen={offeneListe}
        kollegen={kollegen}
        gruppen={gruppen}
        meineId={meineId}
        aktiv={ziel}
        onWaehlen={(z) => { setZiel(z); setNeuOffen(false); }}
        onNeu={() => setNeuOffen((v) => !v)}
        neuOffen={neuOffen}
        onGruppe={gruppeGegruendet}
        onFehler={setFehler}
      />

      {/* ── Verlauf ── */}
      <div data-bj="chat" className={`${ziel ? 'flex' : 'hidden sm:flex'} min-w-0 flex-1 flex-col overflow-hidden`}
        style={{ borderRadius: 24, background: 'rgba(255,255,255,0.05)', boxShadow: 'inset 0 0 0 0.5px rgba(255,255,255,0.08)' }}>
        {!ziel ? (
          <div className="flex h-full flex-col items-center justify-center" style={{ gap: 8, color: 'rgba(235,235,245,0.45)' }}>
            <ChatCircle size={26} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Gespräch auswählen</span>
          </div>
        ) : (
          <>
            {ziel.art === 'gruppe' ? (
              gruppe ? (
                <GruppenKopf
                  gruppe={gruppe}
                  kollegen={kollegen}
                  meineId={meineId || ''}
                  onZurueck={() => setZiel(null)}
                  onGeaendert={() => void gruppenNeuLaden()}
                  onVerlassen={gruppeVerlassen}
                  onFehler={setFehler}
                />
              ) : (
                // Gruppen-Ziel, aber (noch) kein Eintrag in gruppen — direkt nach dem Gründen,
                // solange gruppenNeuLaden() noch läuft, oder nachdem man entfernt wurde. Statt
                // fälschlich den 1:1-Kopf zu zeigen, ein klarer eigener Zustand.
                <div className="flex items-center" style={{ gap: 10, padding: '8px 12px', borderBottom: '0.5px solid rgba(255,255,255,0.1)' }}>
                  {zurueckKnopf}
                  <span className="flex shrink-0 items-center justify-center rounded-full" style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.08)', color: 'rgba(235,235,245,0.45)' }}>
                    <UsersThree size={17} />
                  </span>
                  <span className="truncate" style={{ fontSize: 14, color: 'rgba(235,235,245,0.45)' }}>Gruppe nicht mehr verfügbar</span>
                </div>
              )
            ) : (
              <div className="flex items-center" style={{ gap: 10, padding: '8px 12px', borderBottom: '0.5px solid rgba(255,255,255,0.1)' }}>
                {zurueckKnopf}
                <Avatar name={kopfName} bild={partner?.avatar_url} groesse={36} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate" style={{ fontSize: 15, fontWeight: 800 }}>{kopfName}</span>
                  <span className="truncate" style={{ fontSize: 10.5, color: 'rgba(235,235,245,0.4)' }}>wird nach {AUFBEWAHRUNG_TAGE} Tagen gelöscht</span>
                </span>
              </div>
            )}

            <Verlauf
              nachrichten={verlauf}
              meineId={meineId}
              kollegen={kollegen}
              zeigeAbsender={ziel.art === 'gruppe'}
              onLoeschen={(n) => void loeschen(n)}
              onGross={setGross}
              onAnhang={(a) => { void oeffneChatAnhang(a).then((ok) => { if (!ok) setFehler('Unterlage konnte nicht geöffnet werden – evtl. fehlt die Berechtigung oder sie wurde verschoben.'); }); }}
              onDatei={(path) => { void oeffneChatDatei(path).then((ok) => { if (!ok) setFehler('Datei konnte nicht geöffnet werden.'); }); }}
              onAblegen={(path, name) => setAblegen({ path, name })}
              endeRef={endeRef}
            />
            {ablegen && (
              <InAkteAblegenDialog dateiname={ablegen.name} ladeDatei={() => ladeChatDatei(ablegen.path, ablegen.name)}
                onSchliessen={() => setAblegen(null)} onFertig={(m) => { setAblegen(null); setMeldung(m); }} />
            )}
            {meldung && (
              <div role="status" className="flex items-center" style={{ margin: '0 10px 6px', gap: 8, padding: '6px 10px', borderRadius: 12, background: 'rgba(95,211,138,0.16)', color: '#5fd38a', fontSize: 12, fontWeight: 600 }}>
                <span className="min-w-0 flex-1">{meldung}</span>
                <button type="button" onClick={() => setMeldung(null)} aria-label="Meldung schließen" className="cursor-pointer" style={{ border: 'none', background: 'transparent', color: 'inherit', padding: 0 }}><X size={12} weight="bold" /></button>
              </div>
            )}

            {/* Eingabe */}
            <div style={{ padding: '8px 10px 10px', borderTop: '0.5px solid rgba(255,255,255,0.08)' }}>
              {fehler && (
                <div role="alert" className="flex items-start" style={{ marginBottom: 6, gap: 8, padding: '8px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: 'rgba(255,69,58,0.14)', color: '#ff8a80' }}>
                  <span className="min-w-0 flex-1">{fehler}</span>
                  <button type="button" onClick={() => setFehler(null)} aria-label="Meldung schließen" className="cursor-pointer"
                    style={{ border: 'none', background: 'transparent', color: 'inherit', padding: 0 }}>
                    <X size={12} weight="bold" />
                  </button>
                </div>
              )}
              {bild && (
                <div className="flex items-center" style={{ marginBottom: 6, gap: 8, padding: '6px 10px', borderRadius: 12, background: 'rgba(255,255,255,0.07)' }}>
                  {istBildDatei(bild.name) ? <ImageSquare size={14} weight="fill" color="#64d2ff" /> : <FileText size={14} weight="fill" color="#64d2ff" />}
                  <span className="min-w-0 flex-1 truncate" style={{ fontSize: 12, color: 'rgba(235,235,245,0.7)' }}>{bild.name}</span>
                  <button type="button" onClick={() => setBild(null)} aria-label="Anhang entfernen" className="cursor-pointer"
                    style={{ border: 'none', background: 'transparent', color: 'rgba(235,235,245,0.6)', padding: 0 }}>
                    <X size={12} weight="bold" />
                  </button>
                </div>
              )}
              <div className="flex items-end" style={{ gap: 8 }}>
                <input
                  ref={dateiRef}
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.odt,.ods,.txt,.csv"
                  className="hidden"
                  onChange={(e) => { void handleBild(e.target.files?.[0]); e.target.value = ''; }}
                />
                <button
                  type="button"
                  onClick={() => dateiRef.current?.click()}
                  title="Bild oder Datei anhängen (max. 10 MB)"
                  aria-label="Bild oder Datei anhängen"
                  className="flex shrink-0 cursor-pointer items-center justify-center rounded-full"
                  style={{ width: 40, height: 40, border: 'none', background: 'rgba(255,255,255,0.08)', color: 'rgba(235,235,245,0.7)' }}
                >
                  <ImageSquare size={18} />
                </button>
                <div className="pille-feld flex min-w-0 flex-1 items-end" style={{ minHeight: 40, borderRadius: 20, padding: '4px 4px 4px 14px', gap: 6, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.14)' }}>
                  <textarea
                    value={entwurf}
                    onChange={(e) => setEntwurf(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSenden(); }
                    }}
                    rows={1}
                    placeholder="Nachricht"
                    aria-label="Nachricht (Enter sendet)"
                    className="min-w-0 flex-1 resize-none bg-transparent text-white placeholder:text-[rgba(235,235,245,0.45)]"
                    style={{ maxHeight: 96, padding: '6px 0', border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.35 }}
                  />
                  <button
                    type="button"
                    onClick={() => void handleSenden()}
                    disabled={!kannSenden}
                    className="flex shrink-0 cursor-pointer items-center justify-center rounded-full"
                    style={{ width: 32, height: 32, border: 'none', background: FARBE.blau, color: '#fff', opacity: kannSenden || laedt ? 1 : 0.35, transform: kannSenden || laedt ? 'scale(1)' : 'scale(0.85)', transition: 'opacity .2s, transform .25s cubic-bezier(.34,1.56,.64,1)' }}
                    aria-label="Senden"
                  >
                    {laedt ? <SpinnerGap size={15} className="pille-dreh" /> : <ArrowUp size={16} weight="bold" />}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bild groß — mit Herunterladen und In-neuem-Tab-Öffnen */}
      {gross && (
        <BildGross bild={gross} onSchliessen={() => setGross(null)} onHerunterladen={(b) => void herunterladen(b)} />
      )}
    </div>
  );
}
